import React, { useEffect, useState } from "react";
import client, { formatErr } from "../../lib/api";
import { toast } from "sonner";
import * as Icons from "lucide-react";
import { Plus, Trash2, Users, Clock3, Zap } from "lucide-react";

function blank() {
    return {
        title: "",
        description: "",
        xp_reward: 150,
        expires_in_value: 24,
        expires_in_unit: "hours", // "minutes" | "hours"
        duration_seconds: "",
        icon: "Zap",
        badge_code: "",
    };
}

function timeLeft(expires) {
    const ms = new Date(expires).getTime() - Date.now();
    if (ms <= 0) return "Expired";
    const totalSec = Math.floor(ms / 1000);
    if (totalSec < 60) return `${totalSec}s`;
    const h = Math.floor(ms / 3600000);
    const m = Math.floor((ms % 3600000) / 60000);
    if (h >= 24) return `${Math.floor(h / 24)}d ${h % 24}h`;
    if (h > 0) return `${h}h ${m}m`;
    return `${m}m`;
}

export default function AdminQuests() {
    const [list, setList] = useState([]);
    const [show, setShow] = useState(false);
    const [form, setForm] = useState(blank());
    const [completions, setCompletions] = useState({});

    const load = () => client.get("/admin/quests").then((r) => setList(r.data));
    useEffect(() => {
        load();
    }, []);

    const create = async (e) => {
        e.preventDefault();
        try {
            const value = Math.max(1, Number(form.expires_in_value) || 0);
            const body = {
                title: form.title,
                description: form.description,
                xp_reward: Number(form.xp_reward),
                icon: form.icon,
                badge_code: form.badge_code || null,
                expires_in_minutes: form.expires_in_unit === "minutes" ? value : null,
                expires_in_hours: form.expires_in_unit === "hours" ? value : null,
                duration_seconds: form.duration_seconds ? Number(form.duration_seconds) : null,
            };
            await client.post("/admin/quests", body);
            toast.success("Quest broadcast to all users");
            setForm(blank());
            setShow(false);
            load();
        } catch (err) {
            toast.error(formatErr(err));
        }
    };

    const remove = async (id) => {
        if (!window.confirm("Delete this quest and all completions?")) return;
        await client.delete(`/admin/quests/${id}`);
        toast.success("Deleted");
        load();
    };

    const loadCompletions = async (id) => {
        if (completions[id]) {
            setCompletions((c) => ({ ...c, [id]: null }));
            return;
        }
        const { data } = await client.get(`/admin/quests/${id}/completions`, { params: { limit: 200 } });
        setCompletions((c) => ({ ...c, [id]: data.items }));
    };

    return (
        <div>
            <div className="flex items-end justify-between">
                <div>
                    <span className="label-cap text-xs text-neon-orange">Broadcast</span>
                    <h1 className="font-display font-black text-4xl uppercase tracking-tight mt-1">Quests</h1>
                    <p className="text-zinc-400 text-sm mt-1">
                        Push a time-sensitive challenge to every user. They get a notification + dashboard card.
                    </p>
                </div>
                <button
                    onClick={() => setShow(!show)}
                    data-testid="adm-new-quest"
                    className="btn-neon px-4 py-2 flex items-center gap-2"
                >
                    <Plus size={16} /> New
                </button>
            </div>

            {show && (
                <form onSubmit={create} className="mt-6 surface-2 p-4 grid grid-cols-2 gap-3" data-testid="adm-quest-form">
                    <Field label="Title" full>
                        <input
                            required
                            data-testid="qf-title"
                            value={form.title}
                            onChange={(e) => setForm({ ...form, title: e.target.value })}
                            placeholder="Flash 50 Squat Sprint"
                            className={inp}
                        />
                    </Field>
                    <Field label="Description" full>
                        <textarea
                            required
                            rows={2}
                            data-testid="qf-desc"
                            value={form.description}
                            onChange={(e) => setForm({ ...form, description: e.target.value })}
                            placeholder="Hit 50 squats. Lock in within 4 hours. No video required."
                            className={inp}
                        />
                    </Field>
                    <Field label="XP Reward">
                        <input
                            type="number"
                            min={10}
                            max={2000}
                            data-testid="qf-xp"
                            value={form.xp_reward}
                            onChange={(e) => setForm({ ...form, xp_reward: +e.target.value })}
                            className={inp}
                        />
                    </Field>
                    <Field label="Expires In">
                        <div className="flex gap-2">
                            <input
                                type="number"
                                min={1}
                                data-testid="qf-expires-value"
                                value={form.expires_in_value}
                                onChange={(e) => setForm({ ...form, expires_in_value: +e.target.value })}
                                className={inp + " flex-1 min-w-0"}
                            />
                            <select
                                data-testid="qf-expires-unit"
                                value={form.expires_in_unit}
                                onChange={(e) => {
                                    const unit = e.target.value;
                                    // Sensible default per unit so admins don't accidentally set
                                    // 24-minute quests when switching from hours.
                                    const defaultValue = unit === "minutes" ? 15 : 24;
                                    setForm({ ...form, expires_in_unit: unit, expires_in_value: defaultValue });
                                }}
                                className={inp + " w-32 shrink-0"}
                            >
                                <option value="minutes">Minutes</option>
                                <option value="hours">Hours</option>
                            </select>
                        </div>
                    </Field>
                    <Field label="Intensity Cap (seconds, optional)">
                        <input
                            type="number"
                            min={0}
                            max={86400}
                            data-testid="qf-duration"
                            value={form.duration_seconds}
                            onChange={(e) => setForm({ ...form, duration_seconds: e.target.value })}
                            placeholder="e.g. 300 for a 5-minute sprint"
                            className={inp}
                        />
                    </Field>
                    <Field label="Icon (lucide name)">
                        <input
                            data-testid="qf-icon"
                            value={form.icon}
                            onChange={(e) => setForm({ ...form, icon: e.target.value })}
                            placeholder="Zap, Flame, Skull, Target"
                            className={inp}
                        />
                    </Field>
                    <Field label="Bonus Badge Code (optional)">
                        <input
                            data-testid="qf-badge"
                            value={form.badge_code}
                            onChange={(e) => setForm({ ...form, badge_code: e.target.value })}
                            placeholder="e.g. comeback_king"
                            className={inp}
                        />
                    </Field>
                    <button type="submit" data-testid="qf-submit" className="btn-primary py-3 col-span-2">
                        Broadcast Quest To All Users
                    </button>
                </form>
            )}

            <div className="mt-6 space-y-3">
                {list.length === 0 ? (
                    <div className="surface-1 p-6 text-center text-zinc-400">
                        No quests yet. Push your first time-sensitive challenge.
                    </div>
                ) : (
                    list.map((q) => {
                        const Icon = Icons[q.icon] || Zap;
                        const left = timeLeft(q.expires_at);
                        const expired = left === "Expired";
                        return (
                            <div
                                key={q.id}
                                data-testid={`adm-quest-${q.id}`}
                                className={`surface-1 p-4 ${expired ? "opacity-60" : ""}`}
                            >
                                <div className="flex items-start gap-3">
                                    <div
                                        className="w-10 h-10 flex items-center justify-center shrink-0"
                                        style={{
                                            background: expired ? "#26262640" : "#FF3B3020",
                                            border: `1px solid ${expired ? "#444" : "#FF3B30"}`,
                                        }}
                                    >
                                        <Icon size={16} className={expired ? "text-zinc-500" : "text-neon-orange"} />
                                    </div>
                                    <div className="flex-1 min-w-0">
                                        <div className="font-display font-bold uppercase text-base leading-none">
                                            {q.title}
                                        </div>
                                        <div className="text-xs text-zinc-400 mt-1 line-clamp-2">{q.description}</div>
                                        <div className="flex items-center gap-3 mt-2 text-[11px] text-zinc-500 label-cap flex-wrap">
                                            <span className="flex items-center gap-1">
                                                <Clock3 size={10} /> {left}
                                            </span>
                                            <span className="flex items-center gap-1 text-neon-green">
                                                <Zap size={10} /> +{q.xp_reward} XP
                                            </span>
                                            {q.duration_seconds ? (
                                                <span
                                                    className="flex items-center gap-1 text-neon-orange"
                                                    data-testid={`adm-quest-duration-${q.id}`}
                                                >
                                                    Intensity {Math.floor(q.duration_seconds / 60)}:{String(q.duration_seconds % 60).padStart(2, "0")}
                                                </span>
                                            ) : null}
                                        </div>
                                    </div>
                                    <button
                                        onClick={() => remove(q.id)}
                                        data-testid={`adm-quest-del-${q.id}`}
                                        className="w-9 h-9 surface-3 flex items-center justify-center hover:border-neon-orange shrink-0"
                                    >
                                        <Trash2 size={14} className="text-neon-orange" />
                                    </button>
                                </div>
                                <button
                                    onClick={() => loadCompletions(q.id)}
                                    data-testid={`adm-quest-completions-${q.id}`}
                                    className="mt-3 w-full surface-3 py-2 label-cap text-[10px] flex items-center justify-center gap-2 hover:border-neon-green"
                                >
                                    <Users size={12} />
                                    {completions[q.id] ? "Hide" : "Show"} Completions
                                </button>
                                {completions[q.id] && (
                                    <div className="mt-2 space-y-1">
                                        {completions[q.id].length === 0 ? (
                                            <div className="text-xs text-zinc-500 text-center py-2">No completions yet</div>
                                        ) : (
                                            completions[q.id].map((c) => (
                                                <div key={c.id} className="text-xs flex justify-between border-b border-charcoal-500 py-1">
                                                    <span className="text-zinc-300">{c.user_name}</span>
                                                    <span className="text-zinc-500">{new Date(c.completed_at).toLocaleString()}</span>
                                                </div>
                                            ))
                                        )}
                                    </div>
                                )}
                            </div>
                        );
                    })
                )}
            </div>
        </div>
    );
}

const inp = "w-full bg-charcoal-700 border border-charcoal-500 px-3 py-2 text-white text-sm focus:border-neon-orange focus:outline-none";

function Field({ label, full, children }) {
    return (
        <div className={full ? "col-span-2" : ""}>
            <label className="label-cap text-[10px] text-zinc-500">{label}</label>
            <div className="mt-1">{children}</div>
        </div>
    );
}
